import { useCallback, useEffect, useState } from 'react';

// The "keep several records open" workflow: opening a detail route adds a dismissible tab.
// Tabs live in sessionStorage (per browser tab, cleared when it closes) so a refresh keeps
// your open records but a brand-new session starts clean. Pinned tabs survive navigation
// away; unpinned ones are transient working context.

export interface RecordTab {
  /** Route path (the tab's identity). */
  path: string;
  label: string;
  pinned?: boolean;
}

const KEY = 'hf.tabs';
const MAX = 12;

function read(): RecordTab[] {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return [];
    const v = JSON.parse(raw);
    if (Array.isArray(v)) return v.filter((t) => t && typeof t.path === 'string').slice(0, MAX);
  } catch {
    /* storage unavailable / bad JSON */
  }
  return [];
}

export interface RecordTabsApi {
  tabs: RecordTab[];
  /** Add (or update the label of) a tab. Idempotent by path. */
  open: (path: string, label: string) => void;
  /** Update a tab's label only if it is already open — never re-adds a closed tab. */
  rename: (path: string, label: string) => void;
  close: (path: string) => void;
  togglePin: (path: string) => void;
  closeAll: () => void;
}

export function useRecordTabs(): RecordTabsApi {
  const [tabs, setTabs] = useState<RecordTab[]>(read);

  useEffect(() => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(tabs));
    } catch {
      /* ignore */
    }
  }, [tabs]);

  const open = useCallback((path: string, label: string) => {
    setTabs((cur) => {
      const i = cur.findIndex((t) => t.path === path);
      if (i >= 0) {
        if (cur[i]!.label === label) return cur;
        const next = [...cur];
        next[i] = { ...next[i]!, label };
        return next;
      }
      return [...cur, { path, label }].slice(-MAX);
    });
  }, []);

  const rename = useCallback((path: string, label: string) => {
    setTabs((cur) => {
      const i = cur.findIndex((t) => t.path === path);
      if (i < 0 || cur[i]!.label === label) return cur;
      const next = [...cur];
      next[i] = { ...next[i]!, label };
      return next;
    });
  }, []);

  const close = useCallback((path: string) => setTabs((cur) => cur.filter((t) => t.path !== path)), []);
  const togglePin = useCallback((path: string) => setTabs((cur) => cur.map((t) => (t.path === path ? { ...t, pinned: !t.pinned } : t))), []);
  const closeAll = useCallback(() => setTabs((cur) => cur.filter((t) => t.pinned)), []);

  return { tabs, open, rename, close, togglePin, closeAll };
}
